import { Injectable } from '@angular/core';
import { ContentService } from './content.service';
import { BehaviorSubject } from 'rxjs';
import { GameService } from './game.service';
import { StorageKey, StorageService } from './storage.service';
import { Mission } from '../models/Missions';
import { CollectionResponse } from '../models/Strapi';

@Injectable({
  providedIn: 'root',
})
export class FilterDataService {
  finishedMission = new BehaviorSubject<number[]>([]);
  currentlyShowingMissions = new BehaviorSubject<CollectionResponse<Mission>['data']>([]);
  skippedMissions: number[] = [];
  sliceAmount = 3;

  constructor(
    private contentService: ContentService,
    private gameService: GameService,
    private storageService: StorageService,
  ) {
    const missions = this.storageService.get(StorageKey.FINISHED_MISSIONS);
    if (missions !== null) this.finishedMission.next(missions);
  }

  loadMissions() {
    this.contentService.getMissions().subscribe((r) => {
      if (r) {
        this.updateCurrentlyShowingMissions(r);
      }
    });
  }

  finishMission(newMission: number): void {
    // Add the missions to the list of finished missions
    this.finishedMission.next([...this.finishedMission.value, newMission]);
    this.storageService.set(StorageKey.FINISHED_MISSIONS, this.finishedMission.value);

    // Remove the missions from the list of currently showing missions
    this.currentlyShowingMissions.next(this.currentlyShowingMissions.value.filter((m) => m.id !== newMission));
    this.storageService.set(
      StorageKey.CURRENTLY_SHOWING_MISSIONS,
      this.currentlyShowingMissions.value.map((m) => m.id),
    );
  }

  updateCurrentlyShowingMissions(allMissions: CollectionResponse<Mission>): void {
    const updatedSliceAmountDate = this.storageService.get(StorageKey.UPDATED_SLICE_AMOUNT);
    const daysSinceLastUpdate = this.gameService.calculateDateDifference(updatedSliceAmountDate, new Date());
    const daysSinceStartDate = this.gameService.calculateDateDifference(this.storageService.get(StorageKey.START_DATE), new Date());

    const currentlyShowingMissionsIds = this.storageService.get(StorageKey.CURRENTLY_SHOWING_MISSIONS);

    if (!updatedSliceAmountDate || daysSinceLastUpdate >= 2) {
      const availableMissions = [...allMissions.data].filter(
        (f) =>
          // Check if any dependencies are met
          (!f?.attributes?.dependsOn?.data?.id ||
            f?.attributes?.dependsOn?.data?.id == f?.id ||
            this.finishedMission.value?.includes(f?.attributes?.dependsOn?.data?.id)) &&
          // Check if the mission is not already finished
          !this.finishedMission.value?.includes(f?.id) &&
          // Check if the mission is not skipped
          !this.skippedMissions.includes(f?.id) &&
          (f?.attributes?.showAfterDays ? daysSinceStartDate >= f.attributes.showAfterDays : true),
      );

      const newShowingMissions = [
        ...availableMissions.filter(m => m.attributes.showAfterDays),
        ...this.shuffle(availableMissions.filter(m => !m.attributes.showAfterDays))
      ].slice(0, this.sliceAmount);

      this.storageService.set(
        StorageKey.CURRENTLY_SHOWING_MISSIONS,
        newShowingMissions.map((m) => m.id),
      );

      this.setUpdatedSliceAmountDate();
      this.currentlyShowingMissions.next(newShowingMissions);
    } else if (currentlyShowingMissionsIds) {
      this.currentlyShowingMissions.next(currentlyShowingMissionsIds.map((m) => allMissions.data.find((f) => f.id === m)));
    } else {
      // No currently active missions, the player is done for today
      this.currentlyShowingMissions.next([]);
    }
  }

  shuffle<T extends Array<unknown> = unknown[]>(array: T): T {
    let currentIndex = array.length;

    while (currentIndex != 0) {
      const randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }

    return array;
  }

  setPreviouslyShownMission(mission): void {
    if (!this.storageService.has(StorageKey.PREVIOUSLY_SHOWN_MISSIONS)) {
      this.skippedMissions.push(mission);
      this.storageService.set(StorageKey.PREVIOUSLY_SHOWN_MISSIONS, this.skippedMissions);
      return;
    }

    this.skippedMissions = this.storageService.get(StorageKey.PREVIOUSLY_SHOWN_MISSIONS);

    if (!this.skippedMissions?.includes(mission)) {
      this.skippedMissions?.push(mission);
    }

    this.storageService.set(StorageKey.PREVIOUSLY_SHOWN_MISSIONS, this.skippedMissions);
  }

  /**
   * This method calculates and sets the slice amount (which is the number of missions that will be shown to the user today),
   * based on how close they are to starting their treatment.
   * @param endDateDifference The number of days until the user starts their treatment
   */
  calculateSliceAmount(endDateDifference: number): number {
    switch (endDateDifference) {
      case 1:
        this.sliceAmount = 10;
        break;
      case 2:
        this.sliceAmount = 9;
        break;
      case 3:
        this.sliceAmount = 8;
        break;
      case 4:
        this.sliceAmount = 7;
        break;
      case 5:
        this.sliceAmount = 6;
        break;
      case 6:
        this.sliceAmount = 5;
        break;
      case 7:
        this.sliceAmount = 4;
        break;
      default:
        this.sliceAmount = 3;
        break;
    }
    return this.sliceAmount;
  }

  youtubeIntoEmbedLink(link: string) {
    if (link?.includes('youtu.be') || link?.includes('youtube')) {
      return 'https://youtube.com/embed/' + link?.split('/').pop();
    }
  }

  private setUpdatedSliceAmountDate() {
    const todayMorning = new Date();
    todayMorning.setHours(5, 0, 0, 0);
    this.storageService.set(StorageKey.UPDATED_SLICE_AMOUNT, todayMorning);
  }
}
